/** FnCatalog Component */

import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { debounce, omit } from 'lodash';
import {
  Button,
  Icon,
  HideText,
  P,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import queryString from 'querystring';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import objectToQueryString from '../../Lib/Utils/Helpers/object-to-query-string';
import TableFull from '../ReusableComponents/Table/TableFull';
import FnCatalogTableRow from './FnCatalogTableRow';
import styles from './FnCatalog.module.css';
import Switch from '../ReusableComponents/Switch';
import { catalogItemsExport } from '../../Lib/Utils/Table/table-export';
import BasicModal from '../ReusableComponents/Modals/BasicModal/BasicModal.tsx';
import DimensionsModal from './DimensionsModal/DimensionsModal';
import FnCatalogTableToolbar from './FnCatalogTableToolbar';
import ConfirmationModal from './ConfirmationModal/ConfirmationModal';
import { ReactComponent as InfoSVG } from '../../Assets/icons/info.svg';
import {
  useGetUserCatalogItems,
  useMutateItem,
  useMutateItems,
} from '../../Lib/Hooks/catalog-item.hooks';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

export default function FnCatalog(props) {
  const pathUserId = props.location.pathname.split('/')[2];
  const { currentUser } = props;
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    page: 1,
    offset: 0,
    limit: 50,
    sortBy: ['last_po_receipt_date', 'sku'],
    sortOrder: 'DESC',
  });

  const { isLoading, isFetching, error, data, refetch } =
    useGetUserCatalogItems(pathUserId, params);

  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prop65ModalOpen, setProp65ModalOpen] = useState(false);
  const [confirmProp65, setConfirmProp65] = useState({});
  const [selectedItem, setSelectedItem] = useState();
  const [catalogItems, setCatalogItems] = useState({});

  const updateItemMutation = useMutateItem();
  const updateItemsMutation = useMutateItems();
  const seachValue =
    queryString.parse(props.location.search.slice(1)).search || '';

  const saveItem = (itemUpdate) => {
    setProp65ModalOpen(itemUpdate.prop65 !== null);
    setConfirmProp65(omit(itemUpdate, ['division', 'checked', 'value']));
  };

  const saveItems = (itemsUpdates) => {
    if (!itemsUpdates.length) return;
    setConfirmProp65(
      itemsUpdates.map((item) => omit(item, ['division', 'checked', 'value'])),
    );
    setProp65ModalOpen(true);
  };

  const confirmSaveItem = () => {
    if (!confirmProp65) return;

    if (Array.isArray(confirmProp65)) {
      if (!confirmProp65.length) return;

      const successCb = () => {
        refetch();
      };

      const errorCb = () => {};

      const mutationArgs = {
        currentUser: pathUserId,
        params: confirmProp65,
        successCb,
        errorCb,
      };

      updateItemsMutation.mutate(mutationArgs);
      setConfirmProp65({});
      setProp65ModalOpen(false);
      return;
    }
    const itemUpdate = { ...confirmProp65 };
    updateItemMutation.mutate({ currentUserId: pathUserId, itemUpdate });
    setConfirmProp65({});
    setProp65ModalOpen(false);
  };

  const updateHistory = (newParams) => {
    const {
      history,
      location: { pathname },
    } = props;

    const query = objectToQueryString(newParams);
    history.push({ pathname, search: query });
    refetch();
  };

  useEffect(() => {
    updateHistory(params);
  }, [params]);

  useEffect(() => {
    if (data) {
      setCatalogItems(data);
    }
  }, [data]);

  const perms = useMemo(() => {
    const canExport = hasPermissionTo(
      'users/:id/fn-catalog',
      currentUser.role,
      Actions.READ,
      'EXPORT',
    );
    const canUpdateSizeAndWeight = hasPermissionTo(
      'users/:id/fn-catalog',
      currentUser.role,
      Actions.UPDATE,
      'SIZE_AND_WEIGHT',
    );
    const canUpdateProps = hasPermissionTo(
      'users/:id/fn-catalog',
      currentUser.role,
      Actions.UPDATE,
      'PROPS',
    );
    return {
      canExport,
      canUpdateSizeAndWeight,
      canUpdateProps,
    };
  }, [currentUser]);

  if (error) return `An error has occurred: ${error.message}`;

  const handlePageChange = (page) => {
    setParams({ ...params, page, offset: (page - 1) * params.limit });
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setParams({ ...params, sortBy: [sortBy, 'sku'], sortOrder });
  };

  const handleSwitchChange = (value) => {
    setParams({ ...params, incompleteDimensionsOnly: value });
  };

  const handleSearchChange = (_, search) => {
    setParams({
      ...params,
      styleNumber: search.trim() || undefined,
      page: 1,
      offset: 0,
    });
  };

  const handleExport = () => {
    setExporting(true);

    catalogItemsExport(pathUserId, params).then(() => setExporting(false));
  };

  const handleDimensionOpen = (catalogItem) => {
    setSelectedItem(catalogItem);
    setModalOpen(true);
  };

  const handleItemCheck = (catalogItem) => {
    const updatedCatalogItems = catalogItems.items.map((item) => {
      if (item.id === catalogItem.id) {
        return { ...item, checked: !catalogItem.checked };
      }
      return item;
    });
    setCatalogItems({ count: catalogItems.count, items: updatedCatalogItems });
  };

  const handleAllItemCheck = (newCheckState) => {
    const updatedCatalogItems = catalogItems.items.map((item) => {
      if (item.division?.toLowerCase() === "kid's") return item;
      return {
        ...item,
        checked: newCheckState,
      };
    });
    setCatalogItems({
      count: catalogItems.count,
      items: updatedCatalogItems,
    });
  };

  return (
    <Page.Container>
      <Header.Major icon="list/list-heart" title="Catalog" />
      <Header.Minor
        search={{
          value: seachValue,
          onChange: debounce(
            (value) => handleSearchChange('search', value),
            500,
          ),
        }}
      >
        <Stack direction="row" content="center" align="center" width="none">
          <Switch
            id="fn-catalog-missing-only-switch"
            sx={{ m: 1 }}
            onChange={(_, value) => handleSwitchChange(value)}
          />
          <P size="xs">Show missing only</P>
        </Stack>
        {exporting ? (
          <Icon.LoadingCircle />
        ) : (
          perms.canExport && (
            <Button size="sm" color="secondary" onClick={handleExport}>
              <Icon name="download/download-down" />
              <HideText notAbove="lg">Export</HideText>
            </Button>
          )
        )}
      </Header.Minor>
      <Page.Content>
        <Grid item className={styles.infoContainer}>
          <span>
            <strong className={styles.infoImportantText}>
              Not applicable to Children’s Products.
            </strong>{' '}
            Fashion Nova does NOT accept any children’s products that require
            Prop 65 warning.
            {/* <b>Not applicable to children’s products.</b> Fashion Nova does NOT
            accept any children’s products that require a Prop 65 warning. */}
          </span>
        </Grid>
        {catalogItems.items ? (
          <TableFull
            headerCells={[
              {
                value: 'selected',
                label: '',
                isSortable: false,
                width: '10px',
              },
              {
                value: 'prop65',
                label: (
                  <div className={styles.prop65}>
                    <span>CA PROP 65 </span>
                    <a
                      href="https://www.p65warnings.ca.gov/sites/default/files/art_6_business_qa.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.infoIcon}
                      aria-label="Info"
                    >
                      <InfoSVG className={styles.prop65InfoIcon} />
                    </a>
                  </div>
                ),
                isSortable: false,
                width: '100px',
              },
              {
                value: 'sku',
                label: 'FN ITEM NUMBER',
                isSortable: true,
              },
              {
                value: 'size',
                label: 'Size',
                width: '80px',
              },
              { value: 'dimensions', label: 'SIZE & WEIGHT' },
              {
                value: 'last_po_receipt_date',
                label: 'LAST RECEIVED',
                isSortable: true,
              },
              { value: 'division', label: 'DIVISION' },
              { value: 'wms_number', label: 'WMS NUMBER' },
            ]}
            toolsBar={
              <FnCatalogTableToolbar
                onClickCheckbox={handleAllItemCheck}
                catalogItems={catalogItems.items}
                bulkUpdateCatalogItem={saveItems}
                perms={perms}
              />
            }
            params={params}
            count={catalogItems.count}
            onSortUpdate={handleSortChange}
            onPageChange={handlePageChange}
            loading={isLoading}
          >
            {catalogItems.items.map((catalogItem) => (
              <FnCatalogTableRow
                key={catalogItem.id}
                catalogItem={catalogItem}
                onDimensionOpen={handleDimensionOpen}
                onUpdateItem={saveItem}
                onItemCheck={handleItemCheck}
                loading={isFetching}
                perms={perms}
              />
            ))}
          </TableFull>
        ) : null}
      </Page.Content>
      {modalOpen ? (
        <BasicModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Size + Weight"
        >
          <DimensionsModal
            defaultSelectedItem={selectedItem}
            onClose={() => setModalOpen(false)}
            onSave={() => {
              refetch();
            }}
          />
        </BasicModal>
      ) : null}
      <ConfirmationModal
        open={prop65ModalOpen}
        onClose={() => {
          setConfirmProp65({});
          setProp65ModalOpen(false);
          handleAllItemCheck();
        }}
        onConfirm={() => confirmSaveItem()}
        loading={isLoading}
        modalId="accept-prop65-modal"
        title="Apply California Proposition 65 Label"
        description="Do you confirm you want this item to be under California Prop65 list of items?"
      />
    </Page.Container>
  );
}
